import { useCallback, useEffect, useState } from "react"
import { useSelector } from "react-redux"

import type { UsersLog } from "types/users.types"

import { selectCurrentOrgId } from "features/store/orgSlice"
import { useLazyOrgUsersLogsQuery } from "features/api"

// Not the table's page size: this runs behind a modal, so fewer requests is all that matters.
const PAGE_SIZE = 100

export type AllUsersLogsStatus = "idle" | "loading" | "done" | "error"

interface UseAllUsersLogsOptions {
  fromDate?: string | null
  toDate?: string | null
  enabled: boolean
}

// Every log in the range, page after page, for as long as it is enabled.
export const useAllUsersLogs = ({
  fromDate,
  toDate,
  enabled,
}: UseAllUsersLogsOptions) => {
  const orgId = useSelector(selectCurrentOrgId)
  const [logs, setLogs] = useState<UsersLog[]>([])
  const [fetchedCount, setFetchedCount] = useState(0)
  const [status, setStatus] = useState<AllUsersLogsStatus>("idle")
  const [attempt, setAttempt] = useState(0)
  const [getUsersLogs] = useLazyOrgUsersLogsQuery()

  useEffect(() => {
    if (!enabled || !orgId || !fromDate || !toDate) {
      return
    }
    // Disabling stops the chain: no further page is asked for, and the one in flight is dropped.
    // Not RTK's abort, which leaves the entry rejected for the next request of the same page.
    let isCancelled = false
    const fetchAll = async () => {
      setStatus("loading")
      setFetchedCount(0)
      let results: UsersLog[] = []
      let cursor: string | undefined
      try {
        do {
          const { data, error } = await getUsersLogs(
            {
              org_id: Number(orgId),
              from_date: fromDate,
              to_date: toDate,
              limit: PAGE_SIZE,
              cursor,
            },
            true,
          )
          if (isCancelled) {
            return
          }
          if (error) {
            throw error
          }
          results = results.concat(data?.results ?? [])
          setFetchedCount(results.length)
          cursor = data?.next || undefined
        } while (cursor)
        setLogs(results)
        setStatus("done")
      } catch {
        if (!isCancelled) {
          setStatus("error")
        }
      }
    }
    void fetchAll()
    // The result stays: the modal still shows it while it fades out, and resets once it is gone.
    return () => {
      isCancelled = true
    }
  }, [enabled, orgId, fromDate, toDate, attempt, getUsersLogs])

  const retry = useCallback(() => setAttempt((current) => current + 1), [])

  // Back to a clean start, so the next run never shows this one's result.
  const reset = useCallback(() => {
    setLogs([])
    setFetchedCount(0)
    setStatus("idle")
  }, [])

  return { logs, fetchedCount, status, retry, reset }
}
